<template>
  <div class="chat-container" :style="{ '--kb-offset': keyboardOffset + 'px', '--app-vvh': viewportHeight + 'px' }">
    <!-- 头部区域 -->
    <div class="header-section">
      <!-- 医生图片 -->
      <div class="doctor-avatar">
        <img src="@/assets/doctor.png" alt="医生" />
      </div>
    </div>

    <!-- 消息列表 -->
    <div class="messages-container" ref="messagesContainer">
      <div class="messages-list">
        <MessageBubble v-for="message in messages" :key="message.id" :message="message" />

        <!-- 加载指示器 -->
        <div v-if="isWaitingForAI" class="loading-indicator">
          <van-loading type="spinner" size="20px">
            AI正在思考中...
          </van-loading>
        </div>
      </div>
    </div>

    <!-- 消息输入 -->
    <MessageInput v-if="!isSessionCompleted" />

    <!-- 欢迎弹窗 -->
    <WelcomeDialog :show="showWelcomeDialog" @update:show="showWelcomeDialog = $event"
      @confirm="handleWelcomeConfirm" />

    <!-- 二维码弹窗 -->
    <QRCodeModal 
      :show="showQRCodeModal" 
      :qr-code-list="qrCodeList" 
      :patient-name="patientName"
      @update:show="showQRCodeModal = $event"
      @close="handleQRCodeClose" 
    />

    <!-- 全屏Loading覆盖层 -->
    <van-overlay :show="isLoading" class="loading-overlay">
      <div class="loading-content">
        <van-loading type="spinner" size="24px" vertical>
          正在初始化问卷...
        </van-loading>
      </div>
    </van-overlay>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { showToast } from 'vant'
import { useChatStore } from '@/stores/chat.js'
import { useAppStore } from '@/stores/app.js'
import { initWechat, isWechatReady, getWechatConfigUrl } from '@/utils/wechat.js'
import { resumeAudioContext } from '@/composables/useAudio.js'
import { useKeyboardOffset } from '@/composables/useKeyboardOffset.js'
import { useAutoScroll } from '@/composables/useAutoScroll.js'
import MessageBubble from './MessageBubble.vue'
import MessageInput from './MessageInput.vue'
import WelcomeDialog from '@/components/common/WelcomeDialog.vue'

import QRCodeModal from '@/components/qr/QRCodeModal.vue'

const chatStore = useChatStore()
const appStore = useAppStore()

// ==================== 状态 ====================

const messagesContainer = ref()
const showWelcomeDialog = ref(false)
const showQRCodeModal = ref(false)

// ==================== 计算属性 ====================

const messages = computed(() => chatStore.messages)
const currentSession = computed(() => chatStore.currentSession)
const hasSession = computed(() => chatStore.hasSession)
const isSessionCompleted = computed(() => chatStore.isSessionCompleted)
const qrCodeList = computed(() => chatStore.qrCodeList)
const isLoading = computed(() => appStore.isLoading) // 全局loading状态
const patientName = computed(() => appStore.userInfo?.name || '') // 获取患者姓名

// 判断是否在等待AI回复
const isWaitingForAI = computed(() => {
  if (!hasSession.value || isSessionCompleted.value) return false
  const lastMessage = messages.value[messages.value.length - 1]
  return lastMessage && lastMessage.sender === 'user'
})

// ==================== 滚动控制 ====================

/**
 * 滚动到底部（仅当内容真正超出可视区域时才滚动）
 */
const { scrollToBottom } = useAutoScroll(messagesContainer)

// 监听消息变化，自动滚动
watch(messages, () => {
  scrollToBottom()
}, { deep: true })

// 键盘弹出/收起时（iOS 视觉视口收缩、Android 布局视口 resize），重新滚到底部
const { keyboardOffset, viewportHeight } = useKeyboardOffset(() => {
  scrollToBottom()
})

// 监听会话完成状态
watch(isSessionCompleted, (completed) => {
  if (completed) {
    console.log('✅ 问诊已完成')
    // 会话完成时，检查二维码列表
    if (qrCodeList.value && qrCodeList.value.length > 0) {
      console.log('✅ 二维码数据已准备完成，显示弹窗')
      showQRCodeModal.value = true
    } else {
      console.warn('⚠️ 会话已完成，但二维码数据为空')
    }
  }
})

// 监听二维码列表变化（兜底方案）
watch(qrCodeList, (newList) => {
  // 如果会话已完成且有二维码数据，确保显示弹窗
  if (isSessionCompleted.value && newList && newList.length > 0 && !showQRCodeModal.value) {
    console.log('✅ 二维码数据更新，显示弹窗（兜底触发）')
    showQRCodeModal.value = true
  }
}, { deep: true })

// ==================== 会话初始化 ====================

/**
 * 初始化会话
 */
const initializeSession = async () => {
  if (!hasSession.value) {
    try {
      await chatStore.startSession()
    } catch (error) {
      console.error('❌ 初始化会话失败:', error)
      showToast({
        type: 'fail',
        message: '问卷初始化失败，请重试',
        duration: 3000
      })
      throw error
    }
  }
}

/**
 * 处理欢迎弹窗确认
 */
const handleWelcomeConfirm = async () => {
  console.log('用户确认开始对话')

  try {
    // 标记用户已交互
    appStore.markUserInteraction()

    // 尝试解锁音频
    try {
      await resumeAudioContext()
      console.log('✅ 音频上下文已解锁')
    } catch (error) {
      console.log('⚠️ 音频解锁失败:', error)
    }

    // 开始问卷会话
    await initializeSession()
  } catch (error) {
    console.error('❌ 开始对话失败:', error)
    // 失败时重新显示欢迎弹窗
    showWelcomeDialog.value = true
  }
}

/**
 * 处理二维码弹窗关闭
 */
const handleQRCodeClose = () => {
  showQRCodeModal.value = false
}

// ==================== 微信SDK初始化 ====================

/**
 * 初始化微信SDK配置
 * 使用全局 wechatService，避免重复初始化和URL不一致问题
 */
const initializeWechatSDK = async () => {
  if (!window.wx) {
    console.error('❌ 微信SDK未加载')
    appStore.setWechatReady(false)
    return false
  }

  try {
    console.log('🔧 ChatContainer: 检查微信SDK状态...')

    // 检查是否已经初始化
    if (isWechatReady()) {
      console.log('✅ 微信SDK已就绪（全局已初始化）')
      appStore.setWechatReady(true)
      return true
    }

    // 尝试初始化（如果main.js中初始化失败）
    console.log('🔄 微信SDK未就绪，尝试初始化...')
    const success = await initWechat()

    appStore.setWechatReady(success)

    if (success) {
      console.log('✅ 微信SDK初始化成功')
      console.log('📝 配置URL:', getWechatConfigUrl())
    } else {
      console.error('❌ 微信SDK初始化失败')
    }

    return success
  } catch (error) {
    console.error('❌ 微信SDK初始化异常:', error)
    appStore.setWechatReady(false)
    return false
  }
}

// ==================== 页面可见性处理 ====================

/**
 * 处理页面可见性变化
 */
const handleVisibilityChange = async () => {
  if (document.hidden) {
    // 页面隐藏时停止音频
    chatStore.stopAudio()
  } else {
    // 页面重新可见时，尝试恢复音频上下文
    if (appStore.userHasInteracted) {
      console.log('🎵 页面重新可见，尝试恢复音频上下文')
      try {
        await resumeAudioContext()
        appStore.markUserInteraction()
      } catch (error) {
        console.log('🎵 恢复音频上下文失败:', error)
      }
    }
  }
}

// ==================== 生命周期 ====================

onMounted(async () => {
  console.log('📱 ChatContainer mounted')

  // 初始化微信SDK
  if (!appStore.wechatReady) {
    await initializeWechatSDK()
  }

  // 显示欢迎弹窗
  showWelcomeDialog.value = true

  // 监听页面可见性
  document.addEventListener('visibilitychange', handleVisibilityChange)

  // 初始滚动
  scrollToBottom()
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  chatStore.stopAudio()
})
</script>

<style lang="scss" scoped>
.chat-container {
  display: flex;
  flex-direction: column;
  // 高度基准用 JS 同步的 --app-vvh（visualViewport.height）而非 100vh：iOS 键盘弹出时
  // layout viewport（100vh 依据）不收缩，只有 visualViewport 收缩——若仍用 100vh，
  // 容器高度不变，iOS 会自动上推整个 layout viewport 让 fixed 输入框可见，
  // 头部与消息区随之被推出可视区（真机复现）。100vh 作为无 JS 时的兜底值。
  height: var(--app-vvh, 100vh);
  background: linear-gradient(177deg, #F5FAFE 0%, #ECF8FE 31%, #F7FBFE 60%, #ECF1FE 93%);
}

.header-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 0 0;
}

.doctor-avatar {
  height: 60px;
  padding-left: 16px;

  img {
    width: 60px;
    height: 60px;
    border-radius: 8px;
    object-fit: cover;
  }
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  // 键盘弹出时让出空间的职责完全交给 .chat-container 的 --app-vvh 高度收缩
  // （见上方 .chat-container 注释）。这里不再额外叠加 margin-bottom: var(--kb-offset)——
  // 那是第二轮遗留的补偿，与第三轮的容器收缩同时生效会把同一段键盘高度扣两次，
  // 消息区可用高度被压到 0 甚至负值，导致内容瞬间不可见（第四轮修复）。
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }
}

.messages-list {
  padding: 20px;
  padding-bottom: 80px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  margin-bottom: 20px;

  .van-loading {
    color: #666;
    font-size: 14px;
  }
}

// 移动端适配
@media (max-width: 480px) {
  .messages-list {
    padding: 15px;
    padding-bottom: 70px;
  }

  .loading-indicator {
    padding: 15px;
    margin-bottom: 15px;
  }
}

// 全屏Loading样式
.loading-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  .van-loading {
    color: #1989fa;
    font-size: 14px;
    margin-top: 12px;
  }
}

// 安全区域适配
@supports (padding: max(0px)) {
  .chat-container {
    padding-bottom: max(0px, env(safe-area-inset-bottom));
  }

  .messages-list {
    padding-bottom: max(80px, calc(80px + env(safe-area-inset-bottom)));
  }
}
</style>
